import React from 'react';
import { Link } from 'react-router-dom';
import { locate } from '../layout/menus';

// 平台助手回答的极简 Markdown 渲染 (2026-10-14): 段落 / 标题 / 列表 / 表格 / 粗体 / 行内代码 / 站内链接.
// 全部拼成 React 节点, 不用 innerHTML (模型输出不可信, 杜绝注入);
// 链接只渲染能落到左侧菜单某页的站内路径, 其余 (站外 / 未知路径) 一律降为纯文本; 不渲染图片.

const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

/** 站内路径且能定位到某个菜单页才渲染为链接. */
function isInternalRoute(href: string): boolean {
    if (!href.startsWith('/') || href.startsWith('//')) return false;
    return !!locate(href.split(/[?#]/)[0]);
}

function renderInline(text: string, keyBase: string): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    let last = 0;
    let i = 0;
    for (const m of text.matchAll(INLINE_RE)) {
        const tok = m[0];
        const at = m.index ?? 0;
        if (at > last) out.push(text.slice(last, at));
        const key = `${keyBase}-${i++}`;
        if (tok.startsWith('**')) {
            out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
        } else if (tok.startsWith('`')) {
            out.push(
                <code
                    key={key}
                    className="px-1 py-px rounded border border-violet-100 bg-white text-violet-700 text-[12px]"
                >
                    {tok.slice(1, -1)}
                </code>,
            );
        } else {
            const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
            const label = lm?.[1] ?? tok;
            const href = lm?.[2] ?? '';
            out.push(
                isInternalRoute(href) ? (
                    <Link
                        key={key}
                        to={href}
                        className="text-violet-700 font-medium underline decoration-violet-300 underline-offset-2 hover:decoration-violet-600"
                    >
                        {label}
                    </Link>
                ) : (
                    <span key={key}>{label}</span>
                ),
            );
        }
        last = at + tok.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

const isTableRow = (l: string) => l.trim().startsWith('|');
const isSepRow = (l: string) => /^\s*\|?\s*:?-{2,}/.test(l);
const cells = (l: string) =>
    l
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c) => c.trim());
const isFence = (l: string) => l.trim().startsWith('```');
const LIST_RE = /^\s*(?:[-*]|\d+[.)])\s+/;

interface Props {
    text: string;
}

const MiniMarkdown = ({ text }: Props) => {
    const lines = text.replace(/!\[[^\]]*\]\([^)]*\)/g, '').split('\n');
    const blocks: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const key = `b${i}`;
        if (!line.trim()) {
            i++;
            continue;
        }
        if (isFence(line)) {
            const code: string[] = [];
            i++;
            while (i < lines.length && !isFence(lines[i])) {
                code.push(lines[i]);
                i++;
            }
            i++; // 跳过收尾的 ``` (流式生成中可能还没到, 越界无妨)
            blocks.push(
                <pre
                    key={key}
                    className="bg-white border border-violet-100 rounded-md px-2.5 py-2 text-[12px] text-slate-600 whitespace-pre-wrap"
                >
                    {code.join('\n')}
                </pre>,
            );
            continue;
        }
        const h = /^(#{1,6})\s+(.*)$/.exec(line);
        if (h) {
            blocks.push(
                <div
                    key={key}
                    className="flex items-center gap-1.5 font-semibold text-slate-900 pt-1.5 first:pt-0 before:content-[''] before:w-1 before:h-3.5 before:rounded-full before:bg-violet-500"
                >
                    {renderInline(h[2], key)}
                </div>,
            );
            i++;
            continue;
        }
        if (isTableRow(line)) {
            const rows: string[][] = [];
            while (i < lines.length && isTableRow(lines[i])) {
                if (!isSepRow(lines[i])) rows.push(cells(lines[i]));
                i++;
            }
            const [head, ...body] = rows;
            blocks.push(
                <div
                    key={key}
                    className="overflow-x-auto rounded-md border border-violet-100 bg-white"
                >
                    <table className="w-full text-[12px] border-collapse">
                        <thead>
                            <tr>
                                {head.map((c, j) => (
                                    <th
                                        key={j}
                                        className="border-b border-violet-100 bg-violet-50/60 px-2.5 py-1.5 text-left font-medium text-slate-700"
                                    >
                                        {renderInline(c, `${key}h${j}`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {body.map((r, ri) => (
                                <tr key={ri}>
                                    {r.map((c, j) => (
                                        <td
                                            key={j}
                                            className="border-t border-violet-50 px-2.5 py-1.5 align-top"
                                        >
                                            {renderInline(
                                                c,
                                                `${key}r${ri}c${j}`,
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>,
            );
            continue;
        }
        if (LIST_RE.test(line)) {
            const items: string[] = [];
            const ordered = /^\s*\d/.test(line);
            while (i < lines.length && LIST_RE.test(lines[i])) {
                items.push(lines[i].replace(LIST_RE, ''));
                i++;
            }
            const ListTag = ordered ? 'ol' : 'ul';
            blocks.push(
                <ListTag
                    key={key}
                    className={`${ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1 marker:text-violet-400`}
                >
                    {items.map((it, j) => (
                        <li key={j}>{renderInline(it, `${key}l${j}`)}</li>
                    ))}
                </ListTag>,
            );
            continue;
        }
        const para: string[] = [];
        while (
            i < lines.length &&
            lines[i].trim() &&
            !isTableRow(lines[i]) &&
            !LIST_RE.test(lines[i]) &&
            !/^#{1,6}\s/.test(lines[i]) &&
            !isFence(lines[i])
        ) {
            para.push(lines[i]);
            i++;
        }
        blocks.push(
            <p key={key} className="whitespace-pre-wrap">
                {renderInline(para.join('\n'), key)}
            </p>,
        );
    }
    return <div className="space-y-2 leading-relaxed">{blocks}</div>;
};

export default MiniMarkdown;
